/**
 * Purchase limits (custom.max_purchase_quantity) without patching theme.js.
 * Fetches /cart.js on load, pageshow, and cart updates; corrects quantity
 * inputs after the theme's setQuantityBoundries runs.
 */
(function initPurchaseQuantityLimits(global) {
  'use strict';

  const cartQtyByProduct = new Map();
  const productIdByVariant = new Map();

  const getStoreRoot = () => {
    const r = global.Shopify?.routes?.root;
    if (r == null) return '/';
    return r.endsWith('/') ? r : `${r}/`;
  };

  const getCartQty = (productId) => cartQtyByProduct.get(Number(productId)) || 0;

  const getMaxForProduct = (productId) => {
    const id = Number(productId);
    const row = document.querySelector(`[data-product-id="${id}"][data-purchase-max]`);
    if (row) {
      const max = parseInt(row.getAttribute('data-purchase-max'), 10);
      if (!Number.isNaN(max)) return max;
    }
    const input = document.querySelector(`quantity-input[data-product-id="${id}"] input[data-max]`);
    if (input) {
      const max = parseInt(input.getAttribute('data-max'), 10);
      if (!Number.isNaN(max)) return max;
    }
    return null;
  };

  const canAdd = (productId, qty = 1) => {
    const max = getMaxForProduct(productId);
    if (max == null) return true;
    return getCartQty(productId) + (Number(qty) || 1) <= max;
  };

  const isCartLineInput = (input) =>
    input?.name === 'updates[]' || Boolean(input?.closest('.cart-item, .quick-order-list__quantity'));

  const getProductIdFromInput = (input) => {
    const qi = input.closest('quantity-input[data-product-id]');
    if (qi) return Number(qi.getAttribute('data-product-id'));
    const row = input.closest('[data-product-id]');
    if (row) return Number(row.getAttribute('data-product-id'));
    const variantId = Number(input.getAttribute('data-quantity-variant-id'));
    if (!Number.isNaN(variantId) && productIdByVariant.has(variantId)) {
      return productIdByVariant.get(variantId);
    }
    return NaN;
  };

  const fixCartLineQuantityInput = (input, maxTotal, productId) => {
    const lineQty = parseInt(input.value, 10) || 0;
    const productCartQty = Number.isNaN(productId) ? lineQty : getCartQty(productId);
    const plus = input.closest('quantity-input')?.querySelector('button[name="plus"]');

    if (productCartQty >= maxTotal) {
      input.max = lineQty;
      plus?.setAttribute('disabled', '');
      return;
    }

    const headroom = maxTotal - productCartQty + lineQty;
    input.max = headroom;
    plus?.toggleAttribute('disabled', lineQty >= headroom);
  };

  const fixAddToCartQuantityInput = (input) => {
    const maxTotal = parseInt(input.getAttribute('data-max'), 10);
    if (Number.isNaN(maxTotal)) return;

    const productId = getProductIdFromInput(input);
    let cartQty = parseInt(input.getAttribute('data-cart-quantity') || '0', 10);
    if (!Number.isNaN(productId) && input.hasAttribute('data-cart-product-quantity')) {
      cartQty = parseInt(input.getAttribute('data-cart-product-quantity'), 10);
    } else if (!Number.isNaN(productId)) {
      cartQty = getCartQty(productId);
    }

    const remaining = maxTotal - cartQty;
    const plus = input.closest('quantity-input')?.querySelector('button[name="plus"]');

    if (remaining <= 0) {
      input.max = 0;
      input.min = 0;
      input.value = 0;
      plus?.setAttribute('disabled', '');
      return;
    }

    input.max = remaining;
    const val = parseInt(input.value, 10) || 0;
    plus?.toggleAttribute('disabled', val >= remaining);
  };

  const fixQuantityInput = (input) => {
    if (!input?.hasAttribute('data-max')) return;

    const maxTotal = parseInt(input.getAttribute('data-max'), 10);
    if (Number.isNaN(maxTotal)) return;

    if (isCartLineInput(input)) {
      fixCartLineQuantityInput(input, maxTotal, getProductIdFromInput(input));
      return;
    }

    fixAddToCartQuantityInput(input);
  };

  const updateProductCard = (card) => {
    const productId = Number(
      card.dataset.productId || card.querySelector('[data-product-id]')?.getAttribute('data-product-id')
    );
    const limit = getMaxForProduct(productId);
    if (Number.isNaN(productId) || limit == null) {
      card.classList.remove('purchase-limit-reached');
      return;
    }

    const atLimit = getCartQty(productId) >= limit;
    card.classList.toggle('purchase-limit-reached', atLimit);
    card.querySelectorAll('form[data-type="add-to-cart-form"] [type="submit"]').forEach((btn) => {
      btn.disabled = atLimit;
    });
  };

  const updateProductForms = () => {
    document.querySelectorAll('form[is="product-form"]').forEach((form) => {
      const productId = Number(
        form.dataset.productId ||
          form.closest('product-info')?.querySelector('quantity-input[data-product-id]')?.getAttribute('data-product-id')
      );
      if (Number.isNaN(productId)) return;

      const max = getMaxForProduct(productId);
      const submit = form.querySelector('[type="submit"]');
      if (max == null || !submit || submit.hasAttribute('data-sold-out-disabled')) return;

      submit.disabled = getCartQty(productId) >= max;
    });
  };

  const applyUi = () => {
    document.querySelectorAll('quantity-input input[data-max]').forEach(fixQuantityInput);
    document.querySelectorAll('.product-card').forEach(updateProductCard);
    updateProductForms();
  };

  const refresh = () =>
    fetch(`${getStoreRoot()}cart.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((cart) => {
        cartQtyByProduct.clear();
        productIdByVariant.clear();
        (cart?.items || []).forEach((item) => {
          const pid = Number(item.product_id);
          const vid = Number(item.variant_id);
          const qty = Number(item.quantity) || 0;
          if (!Number.isNaN(vid)) productIdByVariant.set(vid, pid);
          if (Number.isNaN(pid)) return;
          cartQtyByProduct.set(pid, (cartQtyByProduct.get(pid) || 0) + qty);
        });

        document.querySelectorAll('quantity-input input[data-max]').forEach((input) => {
          const productId = getProductIdFromInput(input);
          if (!Number.isNaN(productId)) {
            input.setAttribute('data-cart-product-quantity', String(getCartQty(productId)));
          }
        });

        applyUi();
        requestAnimationFrame(applyUi);
      })
      .catch(() => {});

  const showLimitMessage = (productId) => {
    const max = getMaxForProduct(productId);
    const template =
      global.FoxTheme?.cartStrings?.purchaseLimitReached || 'Limited to [quantity] per order.';
    alert(template.replace('[quantity]', String(max ?? '')));
  };

  const boot = () => {
    refresh();
    global.addEventListener('pageshow', refresh);

    const pubsub = global.FoxTheme?.pubsub;
    if (pubsub?.subscribe && pubsub.PUB_SUB_EVENTS) {
      pubsub.subscribe(pubsub.PUB_SUB_EVENTS.cartUpdate, () => {
        setTimeout(refresh, 0);
      });
      pubsub.subscribe(pubsub.PUB_SUB_EVENTS.quantityBoundries, () => {
        requestAnimationFrame(applyUi);
      });
    }
  };

  global.PurchaseQuantityLimits = {
    refresh,
    canAdd,
    showLimitMessage,
    getCartQty,
  };

  if (global.document.readyState === 'loading') {
    global.document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})(typeof window !== 'undefined' ? window : this);
